import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Button, Form, FormGroup, Input, Label } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { useTeam } from './useTeam';
import { teamErrorMessage } from './teamErrorMessage';
import { readM24Error } from '../music24/utils/parseM24LimitError';
import { LockedControl } from '../music24/components/LockedControl/LockedControl';
import { SMLUpgradeFeature } from '../music24/config/music24PlanCapabilities';
import { useUpgradePrompt } from '../music24/hooks/useUpgradePrompt';
import { useSeatUpgradePlan } from './useSeatUpgradePlan';
import { TeamSeatsFullNote } from './TeamSeatsFullNote';
import { Panel, PanelBody, PanelHead } from '../music24/components/primitives/Panel';

interface TeamInviteFormProps {
  invite: ReturnType<typeof useTeam>['invite'];
  /** `null` when the plan has no seat limit — hides the "N seats left" caption. From `team.list`. */
  seatsLeft: number | null;
  /** `team.list.workspaceName`: named in the description (design: workspaces d1). */
  workspaceName: string;
}

const TeamInviteFormComponent: React.FC<TeamInviteFormProps> = ({ invite, seatsLeft, workspaceName }) => {
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const openUpgradePrompt = useUpgradePrompt();
  const seatUpgrade = useSeatUpgradePlan();
  // Design p4: with no free seat, "Send invite" is locked and opens the upgrade prompt for seats
  // before any request. The server still answers M24_LIMIT_SEATS if the count here is stale.
  const isFull = seatsLeft === 0;
  const canUpgrade = seatUpgrade.nextPlan !== null;
  const openSeatsPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.seats), [openUpgradePrompt]);

  const onSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (isFull) {
        if (canUpgrade) {
          openSeatsPrompt();
        }
        return;
      }
      setSentTo(null);
      const target = email;
      invite.mutate(
        { email: target },
        {
          onSuccess: () => {
            setSentTo(target);
            setEmail('');
          },
        }
      );
    },
    [email, invite, isFull, canUpgrade, openSeatsPrompt]
  );

  // `M24_LIMIT_SEATS` already opens the global upgrade prompt: every tRPC error passes through
  // `dispatchM24LimitPrompt` (`TRPCLoader`'s mutation cache). Every other error gets its own inline
  // alert in this card (design p3): a team refusal, a busy workspace, or a generic failure.
  const errorCode = readM24Error(invite.error)?.code;
  const showInlineError =
    invite.error !== null && !(typeof errorCode === 'string' && errorCode.startsWith('M24_LIMIT_'));
  const submitLabel = intl.formatMessage({ id: 'app.sml.team.invite.submit', defaultMessage: 'Send invite' });

  return (
    <Panel as="section" className="mb-4">
      <PanelHead
        title={intl.formatMessage({ id: 'app.sml.team.invite.title', defaultMessage: 'Invite a teammate' })}
        subtitle={intl.formatMessage(
          {
            id: 'app.sml.team.invite.description',
            defaultMessage:
              'They get an email with a link that works for 7 days. They keep their own workspace and add {workspace} to their workspace menu. Only you manage billing and the team.',
          },
          { workspace: workspaceName }
        )}
        right={
          seatsLeft !== null &&
          intl.formatMessage(
            {
              id: 'app.sml.team.invite.seatsLeft',
              defaultMessage: '{count, plural, one {# seat left} other {# seats left}}',
            },
            { count: seatsLeft }
          )
        }
      />
      <PanelBody>
        <Form onSubmit={onSubmit} className="d-flex flex-wrap gap-2 align-items-start">
          <FormGroup className="mb-0 flex-grow-1">
            <Label for="teamInviteEmail" className="visually-hidden">
              {intl.formatMessage({ id: 'app.sml.team.invite.emailLabel', defaultMessage: 'Email address' })}
            </Label>
            <Input
              id="teamInviteEmail"
              type="email"
              required
              placeholder={intl.formatMessage({
                id: 'app.sml.team.invite.placeholder',
                defaultMessage: 'name@label.com',
              })}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </FormGroup>
          {isFull && canUpgrade ? (
            <LockedControl
              variant="button"
              feature={SMLUpgradeFeature.seats}
              label={submitLabel}
              onClick={openSeatsPrompt}
              omitCap
            />
          ) : (
            <Button color="primary" type="submit" disabled={invite.isPending || isFull}>
              {submitLabel}
            </Button>
          )}
        </Form>
        {isFull && <TeamSeatsFullNote current={seatUpgrade.current} nextPlan={seatUpgrade.nextPlan} />}
        {showInlineError && (
          <Alert color="danger" className="mt-3 mb-0">
            {teamErrorMessage(intl, invite.error)}
          </Alert>
        )}
        {sentTo !== null && (
          <Alert color="success" className="mt-3 mb-0" data-clarity-mask="true">
            {intl.formatMessage(
              { id: 'app.sml.team.invite.sent', defaultMessage: 'Invite sent to {email}' },
              { email: sentTo }
            )}
          </Alert>
        )}
      </PanelBody>
    </Panel>
  );
};

export const TeamInviteForm = TypedMemo(TeamInviteFormComponent);
